// src/pages/finance-portal/reports/reportShared.js
//
// Shared helpers and style tokens for the  reporting pages.

import React from 'react';
import { Link as RouterLink } from 'react-router-dom';
import { Link as MuiLink } from '@mui/material';

export const fontFamily = "'Plus Jakarta Sans', -apple-system, BlinkMacSystemFont, sans-serif";
export const ACCENT = '#22c55e';
export const ACCENT_DARK = '#15803d';
export const DANGER = '#ef4444';
export const WARNING = '#f59e0b';
export const INFO = '#3b82f6';

export function formatCurrency(val, currency = 'USD') {
    const n = Number(val || 0);
    return new Intl.NumberFormat('en-US', {
        style: 'currency',
        currency,
        maximumFractionDigits: 2,
    }).format(n);
}

export function formatNumber(val, digits = 2) {
    const n = Number(val || 0);
    return n.toLocaleString('en-US', {
        minimumFractionDigits: digits,
        maximumFractionDigits: digits,
    });
}

export function formatPercent(val, digits = 2) {
    const n = Number(val || 0);
    return `${n.toFixed(digits)}%`;
}

export function formatDate(dateStr) {
    if (!dateStr) return '—';
    const d = new Date(dateStr);
    if (isNaN(d.getTime())) return dateStr;
    return d.toLocaleDateString('en-US', {
        month: 'short', day: 'numeric', year: 'numeric',
    });
}

export function todayIso() {
    return new Date().toISOString().slice(0, 10);
}

export function firstOfYearIso(year) {
    const y = year || new Date().getFullYear();
    return `${y}-01-01`;
}

export function firstOfMonthIso() {
    const d = new Date();
    d.setDate(1);
    return d.toISOString().slice(0, 10);
}

export function downloadCsv(filename, rows, columns) {
    if (!rows || !rows.length) return;
    const cols = columns || Object.keys(rows[0]);
    const header = cols.join(',');
    const escape = (v) => {
        if (v === null || v === undefined) return '';
        const s = String(v).replace(/"/g, '""');
        return /[",\n]/.test(s) ? `"${s}"` : s;
    };
    const body = rows.map((r) => cols.map((c) => escape(r[c])).join(',')).join('\n');
    const csv = `${header}\n${body}`;
    const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = filename.endsWith('.csv') ? filename : `${filename}.csv`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
}

// Renders an account label as a link into the GL Detail report, pre-filtered
// to that account and the caller's period. Pass `asOf` for cumulative
// "as-of-a-date" reports (Balance Sheet, Trial Balance); pass
// `startDate`/`endDate` for period-based reports (P&L, Statement of Equity).
export function AccountLink({ accountId, label, startDate, endDate, asOf, sx }) {
    if (!accountId) return label;
    const params = new URLSearchParams();
    params.set('account_id', String(accountId));
    if (asOf) {
        params.set('start_date', '2000-01-01');
        params.set('end_date', asOf);
    } else {
        if (startDate) params.set('start_date', startDate);
        if (endDate) params.set('end_date', endDate);
    }
    return (
        <MuiLink
            component={RouterLink}
            to={`/finance-portal/reports/gl-detail?${params.toString()}`}
            sx={{
                color: ACCENT,
                textDecoration: 'none',
                fontWeight: 500,
                '&:hover': { textDecoration: 'underline' },
                ...(sx || {}),
            }}
        >
            {label}
        </MuiLink>
    );
}

export function printReport(elementId) {
    const node = document.getElementById(elementId);
    if (!node) return;
    const win = window.open('', '_blank', 'width=900,height=700');
    if (!win) return;
    win.document.write(`
        <html>
            <head>
                <title>Report</title>
                <style>
                    body { font-family: ${fontFamily}; padding: 24px; color: #111827; }
                    table { width: 100%; border-collapse: collapse; font-size: 12px; }
                    th, td { padding: 8px 10px; border-bottom: 1px solid #e5e7eb; text-align: left; }
                    th { background: #f3f4f6; font-weight: 600; }
                    tr.subtotal td { font-weight: 600; border-top: 2px solid #d1d5db; }
                    tr.total td { font-weight: 700; border-top: 2px solid #111827; }
                    .right { text-align: right; }
                    h1 { font-size: 20px; margin: 0 0 8px; }
                    h2 { font-size: 16px; margin: 18px 0 8px; }
                </style>
            </head>
            <body>${node.innerHTML}</body>
        </html>
    `);
    win.document.close();
    win.focus();
    win.print();
}
